import React from 'react';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ExpertisePage = () => {
  const expertiseAreas = [
    {
      id: 'formation',
      title: 'Formation & Développement',
      subtitle: 'Accélérer la Performance',
      description: 'Des programmes sur mesure qui transforment les compétences de vos équipes et maximisent leur potentiel. Investissement immédiat en croissance humaine.',
      icon: 'GraduationCap',
      color: 'blue'
    },
    {
      id: 'marketing',
      title: 'Marketing Digital',
      subtitle: 'Visibilité & Conversion',
      description: 'Des stratégies digitales complètes (SEO, SEA, Social Media) pour dominer votre marché en ligne et générer un retour sur investissement mesurable. Faites de votre présence un levier de vente.',
      icon: 'TrendingUp',
      color: 'green'
    },
    {
      id: 'ia',
      title: 'Intelligence Artificielle (IA)',
      subtitle: 'Innovation & Efficacité',
      description: 'Intégration stratégique de l\'IA pour automatiser, optimiser vos processus et créer des services innovants. Entrez dans l\'ère de la performance augmentée.',
      icon: 'Cpu',
      color: 'purple'
    },
    {
      id: 'management',
      title: 'Management de Projets Numériques',
      subtitle: 'Réussite Garantie',
      description: 'Méthodologies agiles et expertise de pointe pour mener vos projets numériques (logiciels, plateformes, transformation IT) à terme, dans les délais et budgets. Zéro surprise, livraison optimale.',
      icon: 'Settings',
      color: 'orange'
    },
    {
      id: 'rh',
      title: 'Gestion des RH',
      subtitle: 'Ressources Stratégiques',
      description: 'Audit, optimisation et digitalisation de vos processus RH pour attirer, développer et fidéliser les meilleurs talents. Placez l\'humain au cœur de votre succès durable.',
      icon: 'Users',
      color: 'teal'
    },
    {
      id: 'business-therapie',
      title: 'Business Thérapie',
      subtitle: 'Résilience & Transformation',
      description: 'Un accompagnement ciblé pour surmonter les crises, redéfinir la vision stratégique et aligner la culture d\'entreprise sur les objectifs de croissance. Guérissez les blocages pour libérer le potentiel.',
      icon: 'Heart',
      color: 'red'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        title: 'text-blue-800',
        accent: 'bg-blue-600',
        gradient: 'from-blue-600 to-blue-800'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        title: 'text-green-800',
        accent: 'bg-green-600',
        gradient: 'from-green-600 to-green-800'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: 'text-purple-600',
        title: 'text-purple-800',
        accent: 'bg-purple-600',
        gradient: 'from-purple-600 to-purple-800'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        icon: 'text-orange-600',
        title: 'text-orange-800',
        accent: 'bg-orange-600',
        gradient: 'from-orange-600 to-orange-800'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        icon: 'text-teal-600',
        title: 'text-teal-800',
        accent: 'bg-teal-600',
        gradient: 'from-teal-600 to-teal-800'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        title: 'text-red-800',
        accent: 'bg-red-600',
        gradient: 'from-red-600 to-red-800'
      }
    };
    return colorMap?.[color] || colorMap?.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Domaines d'Expertise
            </h1>
            <p className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto">
              Une expertise multidisciplinaire pour transformer votre organisation et maximiser votre performance
            </p>
          </div>
        </div>
      </section>
      {/* Expertise Areas Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertiseAreas?.map((area) => {
              const colors = getColorClasses(area?.color);
              
              return (
                <div
                  key={area?.id}
                  className={`${colors?.bg} ${colors?.border} border-2 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group`}
                >
                  {/* Icon and Title */}
                  <div className="text-center mb-6">
                    <div className={`w-20 h-20 ${colors?.accent} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={area?.icon} size={40} className="text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${colors?.title} mb-2`}>
                      {area?.title}
                    </h3>
                    <div className={`w-16 h-1 ${colors?.accent} rounded-full mx-auto mb-4`}></div>
                    <h4 className={`text-lg font-semibold ${colors?.icon} uppercase tracking-wide`}>
                      {area?.subtitle}
                    </h4>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 text-center leading-relaxed">
                    {area?.description}
                  </p>

                  {/* CTA Button */}
                  <div className="mt-8 text-center">
                    <button className={`bg-gradient-to-r ${colors?.gradient} text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105`}>
                      En savoir plus
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Impact en Chiffres
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des résultats concrets qui témoignent de notre expertise
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">500+</div>
              <p className="text-gray-600 font-medium">Projets Réalisés</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">98%</div>
              <p className="text-gray-600 font-medium">Satisfaction Client</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-green-600 mb-2">15+</div>
              <p className="text-gray-600 font-medium">Années d'Expérience</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-purple-600 mb-2">1000+</div>
              <p className="text-gray-600 font-medium">Professionnels Formés</p>
            </div>
          </div>
        </div>
      </section>
      {/* Process Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Approche
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Une méthodologie éprouvée pour garantir votre succès
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Analyse', description: 'Diagnostic approfondi de vos besoins et enjeux', icon: 'Search' },
              { step: '02', title: 'Stratégie', description: 'Élaboration d\'une feuille de route personnalisée', icon: 'Target' },
              { step: '03', title: 'Exécution', description: 'Mise en œuvre avec accompagnement expert', icon: 'Play' },
              { step: '04', title: 'Optimisation', description: 'Suivi et amélioration continue des résultats', icon: 'TrendingUp' }
            ]?.map((process, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-6xl font-bold text-red-100 mb-4">{process?.step}</div>
                  <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon name={process?.icon} size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{process?.title}</h3>
                  <p className="text-gray-600">{process?.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à maximiser votre potentiel ?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Découvrez comment notre expertise peut transformer votre organisation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200">
              Consultation Gratuite
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors duration-200">
              Nos Réalisations
            </button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-4 mb-6 md:mb-0">
              <Image
                src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                alt="CRF PERFECTION logo featuring interlocking heart-shaped design representing Centre de Recherche et de Formation excellence"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="text-xl font-bold">CRF PERFECTION</h3>
                <p className="text-gray-400 text-sm">Centre de Recherche et de Formation</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-400 mb-2">
                Excellence • Innovation • Performance
              </p>
              <p className="text-sm text-gray-500">
                © 2024 CRF Perfection. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExpertisePage;